import type { Configuration, PopupRequest } from '@azure/msal-browser';
import { LogLevel } from '@azure/msal-browser';

/**
 * Microsoft Entra External ID configuration for the customer portal.
 *
 * Every value comes from the environment. G-07 says renaming the business is a
 * config change, so no tenant name, domain or client id is compiled in here.
 * The defaults below are the dev tenant only, so a fresh clone runs without
 * setup; staging and production supply their own at build time.
 *
 * Nothing here is a secret. The portal is a public client with no credential,
 * and client ids are public by design (RE-21 is not in play).
 */

function required(name: string, value: string | undefined): string {
  if (!value) {
    throw new Error(
      `${name} is not set. Portal sign-in cannot be configured without it. ` +
        'See .env.development for the dev values.',
    );
  }
  return value;
}

/**
 * Built on demand, never at module scope.
 *
 * An earlier version read the environment as the module loaded and threw from
 * there when a variable was missing. That throw happens during module
 * evaluation, before any component mounts, so it escapes every error boundary
 * and the app renders a white page - and a missing environment variable is the
 * single most likely way this is misconfigured. Building the config inside a
 * function lets the caller catch it and say what is wrong.
 */
export function buildMsalConfig(): Configuration {
  const env = import.meta.env;

  const authority = required('VITE_ENTRA_AUTHORITY', env['VITE_ENTRA_AUTHORITY']);
  const clientId = required('VITE_ENTRA_CLIENT_ID', env['VITE_ENTRA_CLIENT_ID']);

  // ciamlogin.com is not an authority MSAL trusts out of the box, so without
  // knownAuthorities it refuses to redirect and the error does not say why.
  // Derived from the authority rather than configured twice, because two values
  // that must agree are two values that can disagree.
  let authorityHost: string;
  try {
    authorityHost = new URL(authority).host;
  } catch {
    throw new Error(`VITE_ENTRA_AUTHORITY is not a valid URL: ${authority}`);
  }

  return {
  auth: {
    clientId,
    authority,
    knownAuthorities: [authorityHost],

    // Whatever origin the app is served from. Every value here must also be
    // registered on the app registration as a redirect URI, or sign-in fails at
    // the identity provider before the app sees anything.
    redirectUri: globalThis.location?.origin ?? '/',
    postLogoutRedirectUri: globalThis.location?.origin ?? '/',

    // Come back to where the person was, rather than dropping them on the home
    // route after a redirect.
    navigateToLoginRequestUrl: true,
  },

  cache: {
    // sessionStorage, not localStorage: the token dies with the tab. On a shared
    // or family device - which this will be, since adults book for their
    // children - a token surviving in localStorage means the next person to open
    // the browser is still signed in.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },

  system: {
    loggerOptions: {
      logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
      piiLoggingEnabled: false,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        if (level === LogLevel.Error) console.error('[msal]', message);
        else if (level === LogLevel.Warning) console.warn('[msal]', message);
      },
    },
  },
  };
}

/**
 * What to ask for at sign-in.
 *
 * openid and profile are implicit in MSAL's request; the API scope is the one
 * that matters, because it decides who the resulting access token is addressed
 * to. Without it MSAL issues a token for Microsoft Graph instead, which the API
 * rejects with a message about the audience that explains nothing.
 *
 * Also lazy, for the same reason as the configuration above.
 */
export function getLoginRequest(): PopupRequest {
  return { scopes: [required('VITE_API_SCOPE', import.meta.env['VITE_API_SCOPE'])] };
}
